import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getVisionEventImageObjectUrl,
  SensorReading,
  SensorTimelineContext,
  SensorTimelineImage,
  SensorTimelineOven,
} from '../../api/client';
import SensorImageLightbox from './SensorImageLightbox';

const COLORS = ['#ef4444', '#f97316', '#ca8a04', '#16a34a', '#0891b2', '#4f46e5', '#db2777', '#9333ea'];

interface Register {
  key: string;
  label: string;
}

interface Props {
  readings: SensorReading[];
  context: SensorTimelineContext | null;
  start: number;
  end: number;
  selection: [number, number] | null;
  onSelectionChange: (selection: [number, number] | null) => void;
  onZoom: (start: number, end: number) => void;
}

function formatTick(epoch: number, span: number) {
  const date = new Date(epoch);
  return date.toLocaleString('he-IL', span > 24 * 60 * 60 * 1000
    ? { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }
    : { hour: '2-digit', minute: '2-digit', second: span <= 60 * 60 * 1000 ? '2-digit' : undefined });
}

interface ImagePreviewProps {
  image: SensorTimelineImage;
  oven: SensorTimelineOven;
  left: string;
  onOpen: (image: SensorTimelineImage, oven: SensorTimelineOven) => void;
}

function ImagePreview({ image, oven, left, onOpen }: ImagePreviewProps) {
  const [active, setActive] = useState(false);
  const [urls, setUrls] = useState<{ full?: string; roi?: string }>({});
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!active || urls.full || urls.roi || failed) return;
    let disposed = false;
    Promise.all([
      image.has_full_image ? getVisionEventImageObjectUrl(image.event_id, 'full') : Promise.resolve(undefined),
      image.has_roi_image ? getVisionEventImageObjectUrl(image.event_id, 'roi') : Promise.resolve(undefined),
    ]).then(([full, roi]) => {
      if (disposed) {
        if (full) URL.revokeObjectURL(full);
        if (roi) URL.revokeObjectURL(roi);
      } else {
        setUrls({ full, roi });
      }
    }).catch(() => setFailed(true));
    return () => { disposed = true; };
  }, [active, failed, image, urls.full, urls.roi]);

  useEffect(() => () => {
    if (urls.full) URL.revokeObjectURL(urls.full);
    if (urls.roi) URL.revokeObjectURL(urls.roi);
  }, [urls]);

  return (
    <button
      type="button"
      aria-label={`תמונה מתנור ${oven.machine_id} — לחצו להגדלה והורדה`}
      title="לחצו להגדלה והורדה"
      className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 group"
      style={{ left }}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
      onClick={() => onOpen(image, oven)}
    >
      <span className="block w-3 h-3 rounded-full bg-sky-500 border-2 border-white shadow" />
      {active && (
        <span className="absolute bottom-5 left-1/2 -translate-x-1/2 w-72 rounded-xl border border-espresso-200 bg-white p-3 shadow-medium text-right">
          <span className="block font-bold text-xs text-espresso-800">
            תנור {oven.machine_id} · {image.blend_name || 'ללא תערובת'}
          </span>
          <span className="block text-[11px] text-espresso-500 mb-2">
            {new Date(image.captured_at).toLocaleString('he-IL')}
          </span>
          {failed ? (
            <span className="text-xs text-red-600">טעינת התמונות נכשלה</span>
          ) : !urls.full && !urls.roi ? (
            <span className="text-xs text-espresso-400">טוען תמונות…</span>
          ) : (
            <span className="grid grid-cols-2 gap-2" dir="rtl">
              {urls.full && <img src={urls.full} alt="תמונה מלאה" className="w-full h-24 object-contain rounded bg-cream-100" />}
              {urls.roi && <img src={urls.roi} alt="אזור הפולים" className="w-full h-24 object-contain rounded bg-cream-100" />}
            </span>
          )}
        </span>
      )}
    </button>
  );
}

function OvenLane({ oven, start, end, onOpenImage }: {
  oven: SensorTimelineOven;
  start: number;
  end: number;
  onOpenImage: (image: SensorTimelineImage, oven: SensorTimelineOven) => void;
}) {
  const span = Math.max(1, end - start);
  return (
    <div className="flex items-stretch gap-2 py-1.5">
      <div className="w-[72px] shrink-0 flex flex-col justify-center text-xs text-espresso-500">
        <span className="font-bold text-espresso-700">תנור {oven.machine_id}</span>
        <span className="mt-1">תערובת</span>
        <span className="mt-3">תמונות</span>
      </div>
      <div className="flex-1 ml-5 space-y-1.5" dir="ltr">
        <div className="relative h-8 rounded-lg bg-cream-100 border border-cream-300 overflow-hidden">
          {oven.blend_intervals.map(interval => {
            const from = Math.max(start, Date.parse(interval.start_time));
            const to = Math.min(end, Date.parse(interval.end_time));
            if (to <= from) return null;
            return (
              <div
                key={`${interval.batch_id}-${interval.start_time}`}
                className="absolute inset-y-0 bg-gold-200/90 border-x border-gold-600 flex items-center justify-center overflow-hidden px-1"
                style={{ left: `${((from - start) / span) * 100}%`, width: `${((to - from) / span) * 100}%` }}
                title={`${interval.blend_name} · ${new Date(interval.start_time).toLocaleString('he-IL')}`}
              >
                <span className="text-[11px] font-semibold text-espresso-800 truncate">{interval.blend_name}</span>
              </div>
            );
          })}
        </div>
        <div className="relative h-6 rounded-lg bg-sky-50/60 border border-sky-100 overflow-visible">
          {oven.images.map(image => {
            const at = Date.parse(image.captured_at);
            if (at < start || at > end) return null;
            return (
              <ImagePreview
                key={image.event_id}
                image={image}
                oven={oven}
                left={`${((at - start) / span) * 100}%`}
                onOpen={onOpenImage}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function SensorTimelineChart({
  readings,
  context,
  start,
  end,
  selection,
  onSelectionChange,
  onZoom,
}: Props) {
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);
  const [hiddenRegisters, setHiddenRegisters] = useState<Set<string>>(() => new Set());
  // Held here rather than in ImagePreview so the viewer outlives the hover popover.
  const [lightbox, setLightbox] = useState<{ image: SensorTimelineImage; oven: SensorTimelineOven } | null>(null);
  const span = end - start;

  const registers = useMemo<Register[]>(() => {
    const map = new Map<string, string>();
    readings.forEach(reading => {
      const key = `${reading.device_id}|${reading.register_address}`;
      const name = reading.register_name && reading.register_name !== reading.register_address
        ? reading.register_name
        : `Reg ${reading.register_address}`;
      map.set(key, name);
    });
    return Array.from(map, ([key, label]) => ({ key, label }));
  }, [readings]);

  const data = useMemo(() => {
    const points = new Map<number, Record<string, number>>();
    readings.forEach(reading => {
      const time = Date.parse(reading.time);
      const value = Number(reading.value);
      if (!Number.isFinite(time) || !Number.isFinite(value)) return;
      const point = points.get(time) ?? { timestamp: time };
      point[`${reading.device_id}|${reading.register_address}`] = value;
      points.set(time, point);
    });
    return Array.from(points.values()).sort((a, b) => a.timestamp - b.timestamp);
  }, [readings]);

  const ovens = useMemo(() => {
    const available = context?.ovens ?? [];
    return ['1', '2'].map(machineId =>
      available.find(oven => oven.machine_id === machineId) ?? {
        machine_id: machineId,
        blend_intervals: [],
        images: [],
      }
    );
  }, [context]);

  const chartMove = (state?: { activeLabel?: number | string }) => {
    if (dragStart == null || state?.activeLabel == null) return;
    const value = Number(state.activeLabel);
    setDragEnd(value);
    onSelectionChange([Math.min(dragStart, value), Math.max(dragStart, value)]);
  };

  const finishDrag = () => {
    if (dragStart != null && dragEnd != null && Math.abs(dragEnd - dragStart) > 1000) {
      onZoom(Math.min(dragStart, dragEnd), Math.max(dragStart, dragEnd));
    }
    setDragStart(null);
    setDragEnd(null);
  };

  const toggleRegister = (key: string) => {
    setHiddenRegisters(previous => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div dir="ltr">
      <div className="mb-2 flex flex-wrap justify-center gap-x-4 gap-y-2" dir="rtl" aria-label="הצגת חיישנים">
        {registers.map((register, index) => {
          const hidden = hiddenRegisters.has(register.key);
          return (
            <button
              key={register.key}
              type="button"
              aria-pressed={!hidden}
              title={hidden ? `הצג ${register.label}` : `הסתר ${register.label}`}
              onClick={() => toggleRegister(register.key)}
              className={`flex items-center gap-1.5 text-[11px] transition-opacity ${hidden ? 'opacity-40 line-through' : 'opacity-100'}`}
            >
              <span
                className="inline-block h-0.5 w-4"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              {register.label}
            </button>
          );
        })}
      </div>
      <ResponsiveContainer width="100%" height={360}>
        <LineChart
          data={data}
          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
          onMouseDown={state => {
            if (state?.activeLabel == null) return;
            const value = Number(state.activeLabel);
            setDragStart(value);
            setDragEnd(value);
            onSelectionChange([value, value]);
          }}
          onMouseMove={chartMove}
          onMouseUp={finishDrag}
          onMouseLeave={finishDrag}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5ded3" />
          <XAxis
            type="number"
            dataKey="timestamp"
            domain={[start, end]}
            allowDataOverflow
            tickFormatter={value => formatTick(value, span)}
            tick={{ fontSize: 10, fill: '#6b5f52' }}
          />
          <YAxis width={60} tick={{ fontSize: 10, fill: '#6b5f52' }} domain={['auto', 'auto']} />
          <Tooltip
            labelFormatter={value => new Date(Number(value)).toLocaleString('he-IL')}
            formatter={(value: number, key: string) => [value, registers.find(item => item.key === key)?.label ?? key]}
            contentStyle={{ fontSize: 12, borderRadius: 12, borderColor: '#e2d0b3' }}
          />
          {(selection || (dragStart != null && dragEnd != null)) && (
            <ReferenceArea
              x1={(selection ?? [dragStart!, dragEnd!])[0]}
              x2={(selection ?? [dragStart!, dragEnd!])[1]}
              fill="#c49a3c"
              fillOpacity={0.22}
              stroke="#a87920"
            />
          )}
          {registers.map((register, index) => (
            <Line
              key={register.key}
              dataKey={register.key}
              name={register.key}
              stroke={COLORS[index % COLORS.length]}
              dot={false}
              strokeWidth={2}
              connectNulls
              hide={hiddenRegisters.has(register.key)}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-1 border-t border-cream-300 pt-1" dir="rtl">
        {ovens.map(oven => (
          <OvenLane
            key={oven.machine_id}
            oven={oven}
            start={start}
            end={end}
            onOpenImage={(image, source) => setLightbox({ image, oven: source })}
          />
        ))}
      </div>
      {lightbox && (
        <SensorImageLightbox
          image={lightbox.image}
          oven={lightbox.oven}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
